import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Lock, ArrowRight, Eye } from 'lucide-react';
import PresentationRenderer from './dashboard/PresentationRenderer';

const PREVIEW_SLIDE_LIMIT = 5;

// Persistent lock storage per presentation type
const getLockKey = (presentationType) => `preview_lock_${presentationType}`;
const isLockStored = (presentationType) => {
  try {
    return sessionStorage.getItem(getLockKey(presentationType)) === 'locked';
  } catch { return false; }
};
const storeLock = (presentationType) => {
  try {
    sessionStorage.setItem(getLockKey(presentationType), 'locked');
  } catch {}
};

const CoursePreviewModal = ({ isOpen, onClose, presentationType, courseTitle, onScrollToPricing, noLock = false }) => {
  const [isLocked, setIsLocked] = useState(false);
  const containerRef = useRef(null);
  const lastSlideRef = useRef(0);
  const observerRef = useRef(null);

  // Check if already locked from a previous open
  useEffect(() => {
    if (isOpen && !noLock && isLockStored(presentationType)) {
      setIsLocked(true);
    }
  }, [isOpen, presentationType, noLock]);

  const triggerLock = useCallback(() => {
    if (!noLock && !isLocked) {
      setIsLocked(true);
      storeLock(presentationType);
    }
  }, [noLock, isLocked, presentationType]);

  const checkSlideNumber = useCallback(() => {
    if (!containerRef.current || isLocked || noLock) return;

    // Strategy 1: Look for spans with "X / Y" pattern
    const allElements = containerRef.current.querySelectorAll('span, div');
    let bestCurrent = 0;
    let bestTotal = 0;

    for (const el of allElements) {
      // Only check direct text content (not nested)
      const text = el.textContent.trim();
      const match = text.match(/^(\d+)\s*\/\s*(\d+)$/);
      if (match) {
        const current = parseInt(match[1]);
        const total = parseInt(match[2]);
        if (total > bestTotal) {
          bestTotal = total;
          bestCurrent = current;
        }
      }
    }

    if (bestTotal > 0 && bestCurrent > 0) {
      lastSlideRef.current = bestCurrent;
      if (bestCurrent > PREVIEW_SLIDE_LIMIT) {
        triggerLock();
      }
    }
  }, [isLocked, noLock, triggerLock]);

  // Use both MutationObserver AND interval for maximum reliability
  useEffect(() => {
    if (!isOpen || noLock || isLocked) {
      return;
    }

    // Interval polling as primary method
    const interval = setInterval(checkSlideNumber, 200);

    // MutationObserver as secondary method
    if (containerRef.current) {
      observerRef.current = new MutationObserver(() => {
        checkSlideNumber();
      });
      observerRef.current.observe(containerRef.current, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    return () => {
      clearInterval(interval);
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [isOpen, checkSlideNumber, isLocked, noLock]);

  // Also intercept click events on navigation buttons to check after each click
  useEffect(() => {
    if (!isOpen || noLock || isLocked || !containerRef.current) return;

    const handleClick = () => {
      // Check after a short delay to let the slide update
      setTimeout(checkSlideNumber, 100);
      setTimeout(checkSlideNumber, 300);
      setTimeout(checkSlideNumber, 500);
    };

    containerRef.current.addEventListener('click', handleClick, true);
    return () => {
      if (containerRef.current) {
        containerRef.current.removeEventListener('click', handleClick, true);
      }
    };
  }, [isOpen, noLock, isLocked, checkSlideNumber]);

  // When locked, disable all navigation inside the container
  useEffect(() => {
    if (!isLocked || !containerRef.current) return;

    const disableNavigation = () => {
      if (!containerRef.current) return;
      const buttons = containerRef.current.querySelectorAll('button');
      buttons.forEach(btn => {
        btn.disabled = true;
        btn.style.pointerEvents = 'none';
        btn.style.opacity = '0.3';
      });
    };

    disableNavigation();
    // Re-apply periodically in case React re-renders the buttons
    const interval = setInterval(disableNavigation, 500);
    return () => clearInterval(interval);
  }, [isLocked]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Reset only when modal opens fresh (not when closing)
  useEffect(() => {
    if (isOpen && !noLock) {
      // Check stored lock state
      if (isLockStored(presentationType)) {
        setIsLocked(true);
      } else {
        lastSlideRef.current = 0;
      }
    }
  }, [isOpen, presentationType, noLock]);

  if (!isOpen) return null;

  return (
    <div
      data-testid="course-preview-modal"
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
        backdropFilter: 'blur(4px)'
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'white', borderRadius: '16px', overflow: 'hidden',
        width: '100%', maxWidth: '1100px', maxHeight: '90vh',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 25px 50px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '0.75rem 1.25rem',
          background: 'linear-gradient(135deg, #E75480, #C2185B)',
          color: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Eye size={18} />
            <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Aperçu gratuit — {courseTitle}</span>
          </div>
          <button
            data-testid="close-preview-modal"
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%',
              width: '32px', height: '32px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white'
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Presentation container */}
        <div ref={containerRef} style={{ flex: 1, overflow: 'auto', position: 'relative' }}>
          <PresentationRenderer presentationType={presentationType} />

          {/* Lock overlay - covers everything and blocks interaction */}
          {isLocked && (
            <div
              data-testid="preview-lock-overlay"
              style={{
                position: 'absolute', inset: 0,
                background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.85) 40%)',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                padding: '2rem', textAlign: 'center',
                zIndex: 10,
                cursor: 'default'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                background: 'white', borderRadius: '20px', padding: '2.5rem',
                maxWidth: '480px', width: '100%',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
              }}>
                <div style={{
                  width: '56px', height: '56px', borderRadius: '50%',
                  background: 'linear-gradient(135deg, #E75480, #C2185B)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 1.25rem', color: 'white'
                }}>
                  <Lock size={24} />
                </div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.75rem', color: '#1a1a2e' }}>
                  Vous avez vu les {PREVIEW_SLIDE_LIMIT} premières slides
                </h3>
                <p style={{ color: '#64748B', lineHeight: 1.6, marginBottom: '1.5rem', fontSize: '0.95rem' }}>
                  Pour accéder à l'intégralité des cours, quiz et ressources, choisissez la formule qui vous convient.
                </p>
                <button
                  data-testid="preview-see-formulas-btn"
                  onClick={() => {
                    onClose();
                    if (onScrollToPricing) onScrollToPricing();
                  }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                    padding: '0.85rem 2rem', borderRadius: '50px',
                    background: 'linear-gradient(135deg, #E75480, #C2185B)',
                    color: 'white', border: 'none', cursor: 'pointer',
                    fontWeight: 600, fontSize: '1rem',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    boxShadow: '0 4px 15px rgba(231,84,128,0.4)'
                  }}
                  onMouseEnter={e => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 6px 20px rgba(231,84,128,0.5)'; }}
                  onMouseLeave={e => { e.target.style.transform = ''; e.target.style.boxShadow = '0 4px 15px rgba(231,84,128,0.4)'; }}
                >
                  Voir les formules <ArrowRight size={18} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer hint */}
        {!isLocked && (
          <div style={{
            padding: '0.6rem 1.25rem',
            background: '#FFF7ED', borderTop: '1px solid #FED7AA',
            textAlign: 'center', fontSize: '0.8rem', color: '#92400E'
          }}>
            Aperçu limité aux {PREVIEW_SLIDE_LIMIT} premières slides — Naviguez librement pour découvrir le contenu
          </div>
        )}
      </div>
    </div>
  );
};

export default CoursePreviewModal;
